import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  Checkbox,
  Chip,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoorSlidingIcon from '@mui/icons-material/DoorSliding';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import apiClient from '@uniqcamp/shared/services/api.service';
import QrScannerDialog from '../components/QrScannerDialog';
import LocationMapDialog from '../components/LocationMapDialog';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';
import { statusLabel } from '../utils/dispersalConstants';
import type { DispersalListItem, MessageState, StudentOption, LocationState, GateOption, SchoolGatesResponse, StudentAtGate, DispersalsResponse } from '../types';
import { useParentStudents } from '../api/students';

function getDispersalOn(start: string, end: string): boolean {
  if (!start || !end) return true;
  const now = new Date();
  const current = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return current >= start && current <= end;
}

const DispersalAtGate: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { selectedSchoolId } = useSelectedSchool();
  const [, setSchoolCode] = useState('');
  const [, setGateId] = useState('');
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });
  const [scanOpen, setScanOpen] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapMode, setMapMode] = useState<'check' | 'request'>('check');
  const [mapGateId, setMapGateId] = useState<string | null>(null);
  const [mapRequestSource, setMapRequestSource] = useState<'QR_SCAN' | 'MANUAL_REQUEST'>('MANUAL_REQUEST');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Record<string, Set<string>>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const gate = params.get('gate');
    if (code) setSchoolCode(code.toUpperCase());
    if (gate) setGateId(gate);
  }, []);

  useEffect(() => {
    const state = location.state as LocationState | null | undefined;
    if (state?.schoolCode) setSchoolCode(state.schoolCode);
    if (state?.gateId) setGateId(state.gateId);
    if (state?.studentId || state?.schoolCode || state?.gateId) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.pathname, location.state]);

  const { data: studentsData } = useParentStudents(selectedSchoolId || undefined, { enabled: Boolean(selectedSchoolId) });
  const students = useMemo<StudentOption[]>(
    () => (studentsData?.students ?? []).filter((s) => s.viewerCanDispersal !== false),
    [studentsData],
  );

  const { data: schoolGatesData } = useQuery<SchoolGatesResponse>(
    selectedSchoolId ? ['dispersals-school-gates', selectedSchoolId] : 'dispersals-school-gates-disabled',
    () => apiClient.get(`/dispersals/school-gates/${selectedSchoolId}`).then((r: { data: unknown }) => r.data as SchoolGatesResponse),
    { enabled: !!selectedSchoolId }
  );
  const apiSchoolCode = schoolGatesData?.schoolCode ?? '';
  const apiGates = useMemo<GateOption[]>(() => schoolGatesData?.gates ?? [], [schoolGatesData]);
  const schoolGeofence = schoolGatesData?.geofence ?? null;
  const dispersalStart = schoolGatesData?.dispersalStartTime || schoolGatesData?.dispersalStartTime || '14:00';
  const dispersalEnd = schoolGatesData?.dispersalEndTime || schoolGatesData?.dispersalEndTime || '16:00';
  const requestAgainWaitMinutes = Math.max(0, schoolGatesData?.requestAgainWaitMinutes ?? 2);
  const geofenceForQR = schoolGatesData?.geofenceRequiredForQRScan !== false;
  const geofenceForRequest = schoolGatesData?.geofenceRequiredForRequestButton !== false;
  const earlyDismissalGateIds = useMemo<string[]>(() => schoolGatesData?.earlyDismissalGateIds ?? [], [schoolGatesData]);

  const today = new Date().toISOString().slice(0, 10);
  const dispersalsQueryKey = selectedSchoolId ? ['dispersals', today, selectedSchoolId] : ['dispersals', today];
  const { data: dispersalsData } = useQuery<DispersalsResponse>(
    dispersalsQueryKey,
    () => apiClient.get(selectedSchoolId ? `/dispersals?date=${today}&schoolId=${selectedSchoolId}` : `/dispersals?date=${today}`).then((r: { data: unknown }) => r.data as DispersalsResponse)
  );
  const dispersals = useMemo<DispersalListItem[]>(() => dispersalsData?.dispersals ?? [], [dispersalsData]);

  const earlyDismissalsApproved = useMemo(
    () =>
      dispersals.filter(
        (d) =>
          (d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') &&
          (d.status === 'APPROVED_BY_PRINCIPAL' || d.status === 'COMPLETED') &&
          !d.sentFromClass
      ),
    [dispersals]
  );

  const studentsByGate = useMemo((): { gate: GateOption; students: StudentAtGate[] }[] => {
    if (!apiGates.length || !students.length) return [];
    const isEarlyGate = (g: GateOption) => earlyDismissalGateIds.includes(g.gateId);
    return apiGates.map((gate) => {
      const list: StudentAtGate[] = [];
      const cList = gate.allowedClasses || [];
      const addedStudentIds = new Set<string>();
      students.forEach((student) => {
        const c = String(student.class ?? '');
        const s = String(student.section ?? '');
        const ac = cList.find((a) => String(a.class) === c && String(a.section || '') === s && a.enabled !== false);
        if (!ac) {
          // If this gate is an early-dispersal gate, add students with approved early who aren't in allowedClasses
          if (!isEarlyGate(gate)) return;
          const earlyForStudent = earlyDismissalsApproved.find((d) => d.studentId?._id === student._id);
          if (!earlyForStudent) return;
          const earlyTime = earlyForStudent.dispersalTime || earlyForStudent.dismissalTime;
          const pickupTime = earlyTime ? new Date(earlyTime).getTime() : 0;
          const dispersalOn = Date.now() >= pickupTime;
          const dispersal = dispersals.find(
            (d) => d.studentId?._id === student._id && d.gateId === gate.gateId && !d.pickupConfirmed
          );
          const requestedAt = dispersal?.requestedAt || dispersal?.createdAt;
          const waitMs = requestAgainWaitMinutes * 60 * 1000;
          const canRequestAgain = !!(
            dispersal &&
            !dispersal.sentFromClass &&
            requestedAt &&
            Date.now() - new Date(requestedAt).getTime() >= waitMs
          );
          list.push({
            student,
            startTime: earlyTime ? new Date(earlyTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : dispersalStart,
            endTime: dispersalEnd,
            dispersalOn,
            allowRequest: true,
            allowQR: true,
            dispersal,
            canRequestAgain,
            showReceived: !!(dispersal?.sentFromClass && !dispersal.pickupConfirmed),
          });
          addedStudentIds.add(student._id);
          return;
        }
        const startTime = (ac.startTime || '').trim() || dispersalStart;
        const endTime = (ac.endTime || '').trim() || dispersalEnd;
        const dispersalOn = getDispersalOn(startTime, endTime);
        const allowQR = ac.requireQRScanAtGate !== false;
        const allowRequest = !!ac.allowRequestWithoutQR;
        const dispersal = dispersals.find(
          (d) => d.studentId?._id === student._id && d.gateId === gate.gateId && !d.pickupConfirmed
        );
        const requestedAt = dispersal?.requestedAt || dispersal?.createdAt;
        const waitMs = requestAgainWaitMinutes * 60 * 1000;
        const canRequestAgain = !!(
          dispersal &&
          !dispersal.sentFromClass &&
          requestedAt &&
          Date.now() - new Date(requestedAt).getTime() >= waitMs
        );
        const showReceived = !!(dispersal?.sentFromClass && !dispersal.pickupConfirmed);
        list.push({
          student,
          startTime,
          endTime,
          dispersalOn,
          allowRequest,
          allowQR,
          dispersal,
          canRequestAgain,
          showReceived,
        });
        addedStudentIds.add(student._id);
      });
      // Early gate: add any approved-early students not already in list (e.g. gate has no allowedClasses for them)
      if (isEarlyGate(gate)) {
        earlyDismissalsApproved.forEach((earlyD) => {
          const sid = earlyD.studentId?._id;
          if (!sid || addedStudentIds.has(sid)) return;
          const student = students.find((s) => s._id === sid);
          if (!student) return;
          const earlyTimeD = earlyD.dispersalTime || earlyD.dismissalTime;
          const pickupTime = earlyTimeD ? new Date(earlyTimeD).getTime() : 0;
          const dispersalOn = Date.now() >= pickupTime;
          const dispersal = dispersals.find(
            (d) => d.studentId?._id === sid && d.gateId === gate.gateId && !d.pickupConfirmed
          );
          const requestedAt = dispersal?.requestedAt || dispersal?.createdAt;
          const waitMs = requestAgainWaitMinutes * 60 * 1000;
          const canRequestAgain = !!(
            dispersal &&
            !dispersal.sentFromClass &&
            requestedAt &&
            Date.now() - new Date(requestedAt).getTime() >= waitMs
          );
          list.push({
            student,
            startTime: earlyTimeD ? new Date(earlyTimeD).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : dispersalStart,
            endTime: dispersalEnd,
            dispersalOn,
            allowRequest: true,
            allowQR: true,
            dispersal,
            canRequestAgain,
            showReceived: !!(dispersal?.sentFromClass && !dispersal.pickupConfirmed),
          });
        });
      }
      return { gate, students: list };
    }).filter((g) => g.students.length > 0);
  }, [apiGates, students, dispersals, dispersalStart, dispersalEnd, requestAgainWaitMinutes, earlyDismissalGateIds, earlyDismissalsApproved]);

  const confirmPickupMutation = useMutation(
    (dispersalId: string) => apiClient.put(`/dispersals/${dispersalId}/confirm-pickup`).then((r: { data: unknown }) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dispersals', today]);
        if (selectedSchoolId) queryClient.invalidateQueries(['dispersals', today, selectedSchoolId]);
        setMessage({ type: 'success', text: 'Pickup confirmed.' });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } }; message?: string };
        setMessage({ type: 'error', text: e.response?.data?.message ?? e.message ?? 'Error' });
      },
    }
  );

  const requestMutation = useMutation(
    async (payload: { studentIds: string[]; gateId: string; requestSource: 'QR_SCAN' | 'MANUAL_REQUEST'; lat: number; lng: number }) => {
      const { studentIds: sids, gateId: gid, requestSource, lat, lng } = payload;
      const code = apiSchoolCode;
      for (const sid of sids) {
        await apiClient.post('/dispersals/request-from-gate', {
          studentId: sid,
          gateId: gid,
          schoolCode: code,
          latitude: lat,
          longitude: lng,
          requestSource,
          ...(selectedSchoolId && { schoolId: selectedSchoolId }),
        });
      }
    },
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(['dispersals']);
        queryClient.invalidateQueries(['dispersals', today]);
        if (selectedSchoolId) queryClient.invalidateQueries(['dispersals', today, selectedSchoolId]);
        setMessage({ type: 'success', text: 'Request(s) sent. Staff will dismiss from class.' });
        setSelectedStudentIds((prev) => ({ ...prev, [vars.gateId]: new Set() }));
        setMapDialogOpen(false);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string; correctGateName?: string } }; message?: string };
        const msg = e.response?.data?.message ?? e.message ?? '';
        const correctGate = e.response?.data?.correctGateName;
        setMessage({ type: 'error', text: correctGate ? `Go to gate: ${correctGate}. ${msg}` : msg });
      },
    }
  );

  const toggleStudentSelection = (gateId: string, studentId: string, checked: boolean) => {
    setSelectedStudentIds((prev) => {
      const set = new Set(prev[gateId] || []);
      if (checked) set.add(studentId);
      else set.delete(studentId);
      return { ...prev, [gateId]: set };
    });
  };

  const openCheckLocation = (gid: string) => {
    setMapGateId(gid);
    setMapMode('check');
    setMapDialogOpen(true);
  };

  const openRequestFlow = (gid: string, requestSource: 'QR_SCAN' | 'MANUAL_REQUEST') => {
    const set = selectedStudentIds[gid];
    const group = studentsByGate.find((x) => x.gate.gateId === gid);
    const selectable = group?.students.filter((s) => s.dispersalOn && (s.allowRequest || s.allowQR)) ?? [];
    const toUse = set && set.size > 0 ? Array.from(set).filter((id) => selectable.some((s) => s.student._id === id)) : selectable.map((s) => s.student._id);
    if (toUse.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one student with dispersal on.' });
      return;
    }
    setMapGateId(gid);
    setMapRequestSource(requestSource);
    setMapMode('request');
    setMapDialogOpen(true);
  };

  const handleMapConfirm = (latitude: number, longitude: number) => {
    if (!mapGateId || mapMode !== 'request') return;
    const set = selectedStudentIds[mapGateId];
    const group = studentsByGate.find((x) => x.gate.gateId === mapGateId);
    const selectable = group?.students.filter((s) => s.dispersalOn && (s.allowRequest || s.allowQR)) ?? [];
    const toUse = set && set.size > 0 ? Array.from(set).filter((id) => selectable.some((s) => s.student._id === id)) : selectable.map((s) => s.student._id);
    requestMutation.mutate({ studentIds: toUse, gateId: mapGateId, requestSource: mapRequestSource, lat: latitude, lng: longitude });
  };

  const handleRequestAgain = (d: DispersalListItem) => {
    if (!d.studentId?._id || !d.gateId) return;
    setSelectedStudentIds((prev) => ({ ...prev, [d.gateId!]: new Set([d.studentId!._id]) }));
    setMapRequestSource(d.requestSource === 'QR_SCAN' ? 'QR_SCAN' : 'MANUAL_REQUEST');
    setMapGateId(d.gateId);
    setMapMode('request');
    setMapDialogOpen(true);
  };

  const gatesForMap = useMemo(() => (apiGates || []).map((g) => ({
    gateId: g.gateId,
    name: g.name,
    latitude: g.latitude ?? null,
    longitude: g.longitude ?? null,
    requestArea: g.requestArea ?? null,
  })), [apiGates]);
  const requireGeofenceForMap = mapMode === 'request' ? geofenceForRequest : geofenceForQR;

  if (!selectedSchoolId) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>Dispersal Request at School Gate</Typography>
        <Alert severity="info">Please select a school first to request dispersal at gate.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>Dispersal Request at School Gate</Typography>
      {message.text && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {studentsByGate.length === 0 && apiGates.length > 0 && (
        <Alert severity="info">No students are assigned to any gate for your school. Contact the school to configure gates.</Alert>
      )}

      {studentsByGate.map(({ gate, students: gateStudents }) => {
        const selectedSet = selectedStudentIds[gate.gateId] || new Set<string>();

        return (
          <Paper key={gate.gateId} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>{gate.name}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<LocationOnIcon />} onClick={() => openCheckLocation(gate.gateId)}>
                  Check my Location
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openRequestFlow(gate.gateId, 'MANUAL_REQUEST')}
                  disabled={selectedSet.size === 0 || requestMutation.isLoading}
                >
                  Request for dispersal
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => { setSchoolCode(apiSchoolCode); setGateId(gate.gateId); setScanOpen(true); }}
                  disabled={selectedSet.size === 0 || requestMutation.isLoading}
                >
                  Scan QR
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {gateStudents.map(({ student, startTime, endTime, dispersalOn, allowRequest, allowQR, dispersal, canRequestAgain, showReceived }) => {
                const hasAnyDispersalToday = dispersals.some(
                  (d) => d.studentId?._id === student._id && d.gateId === gate.gateId
                );
                const earlyForStudent = earlyDismissalsApproved.find((d) => d.studentId?._id === student._id);
                const earlyPickupStr = earlyForStudent?.dispersalTime || earlyForStudent?.dismissalTime;
                const canSelect = dispersalOn && (allowRequest || allowQR) && !dispersal?.sentFromClass && !dispersal?.pickupConfirmed;
                const isSelected = selectedSet.has(student._id);
                const status = dispersal?.status;
                const requested = dispersal && !dispersal.sentFromClass;
                const showCheckbox = (dispersalOn && (allowRequest || allowQR) && !hasAnyDispersalToday);
                const dispersalForStatus = dispersals.find(
                  (d) => d.studentId?._id === student._id && d.gateId === gate.gateId
                );

                return (
                  <Card key={student._id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      {earlyForStudent && (
                        <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>
                          Early dispersal approved. Pickup time: {earlyPickupStr ? new Date(earlyPickupStr).toLocaleString() : '–'}. Use request below after that time.
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {showCheckbox && (
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => toggleStudentSelection(gate.gateId, student._id, e.target.checked)}
                            disabled={!canSelect}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon color="action" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={600}>{student.name}</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Class {student.class}{student.section ? ` ${student.section}` : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Dispersal Time: {startTime} – {endTime}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <DoorSlidingIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Dispersal Status:{dispersalOn ? ' • On' : ' • Not started'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                            Today&apos;s status: {dispersalForStatus?.pickupConfirmed
                              ? (dispersalForStatus.autoConfirmed ? 'Pickup confirmed (auto)' : 'Pickup confirmed')
                              : showReceived
                                ? 'Dismissed – confirm pickup'
                                : canRequestAgain
                                  ? 'Request again available'
                                  : requested
                                    ? 'Requested'
                                    : dispersalForStatus && dispersalForStatus.status
                                      ? (statusLabel[dispersalForStatus.status] ?? dispersalForStatus.status)
                                      : 'No request today'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {requested && !canRequestAgain && <Chip size="small" label="Requested" color="default" />}
                            {canRequestAgain && (
                              <Button size="small" variant="outlined" onClick={() => handleRequestAgain(dispersal!)}>
                                Request again
                              </Button>
                            )}
                            {showReceived && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => confirmPickupMutation.mutate(dispersal!._id)}
                                disabled={confirmPickupMutation.isLoading}
                              >
                                {confirmPickupMutation.isLoading ? 'Confirming…' : 'Received'}
                              </Button>
                            )}
                            {dispersalForStatus?.pickupConfirmed && (
                              <Chip size="small" label={dispersalForStatus.autoConfirmed ? 'Pickup confirmed (auto)' : 'Pickup confirmed'} color="success" />
                            )}
                            {!requested && !showReceived && !dispersalForStatus?.pickupConfirmed && status && (
                              <Chip size="small" label={statusLabel[status] ?? status} color={status === 'COMPLETED' ? 'success' : 'default'} />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Paper>
        );
      })}

      <QrScannerDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={({ code, gate }) => {
          if (code) setSchoolCode(code);
          if (gate) setGateId(gate);
          setScanOpen(false);
          const g = studentsByGate.find((x) => x.gate.gateId === gate);
          if (g) openRequestFlow(gate, 'QR_SCAN');
        }}
      />

      <LocationMapDialog
        open={mapDialogOpen}
        onClose={() => { setMapDialogOpen(false); setMapGateId(null); }}
        mode={mapMode}
        schoolGeofence={schoolGeofence}
        gates={gatesForMap}
        currentGateId={mapGateId}
        requireGeofence={requireGeofenceForMap}
        onConfirm={mapMode === 'request' ? handleMapConfirm : undefined}
      />
    </Box>
  );
};

export default DispersalAtGate;
